import { IStyle } from 'Styles';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { CustomCheckBox, SelectInput } from 'components/input';
import {
  actionButtonsEnable,
  iconSize,
} from 'components/main/Setup/CrystalContainer/Constants';
import React, { useMemo } from 'react';
import {
  ActionOperationType,
  ActionOperationValue,
  constraintsInitialState,
} from '../constants';
import {
  renderInspectionCheckBoxes,
  renderRadioButtons,
  useOnAddNewAction,
  useOnChangeInspectionCheckBoxValue,
  useOnChangeRadioActions,
  useOnChangeSourceImageStoreValue,
  useOnChangeTargetImageStoreValue,
  useOnChangeVerifyValue,
  useOnUpdateSelectedImageActionId,
} from '../hooks';
import {
  ImageManagementActionDTO,
  SearchConstraintRootDTO,
} from 'business/models/Models';
import { getCurrentOperationType } from '../utils';
import { getNewId } from 'utils/Global';
import {
  useImageManagementTasks,
  useSelectedImageActionId,
  useSelectedImageTaskId,
} from 'redux/hooks/imageManagementTask';
import ActionsBanner from './ActionsBanner';
import { useSetupImageStore } from 'redux/hooks/SetupTab';

const Actions = () => {
  const selectedImageTaskId = useSelectedImageTaskId();
  const imageTasks = useImageManagementTasks();
  const selectedActionId = useSelectedImageActionId();
  const imageStoreList = useSetupImageStore();
  const onAddNewAction = useOnAddNewAction();
  const updateSelectedImageActionId = useOnUpdateSelectedImageActionId();
  const onChangeRadioActions = useOnChangeRadioActions();
  const onChangeSourceImageStoreValue = useOnChangeSourceImageStoreValue();
  const onChangeTargetImageStoreValue = useOnChangeTargetImageStoreValue();
  const onChangeVerifyValue = useOnChangeVerifyValue();
  const onChangeInspectionCheckBoxValue = useOnChangeInspectionCheckBoxValue();

  const actions: ImageManagementActionDTO[] = useMemo(() => {
    if (!selectedImageTaskId) {
      return [];
    }

    const currentImageTaskActions = imageTasks?.find(
      (task) => task.id === selectedImageTaskId,
    )?.imageManagementActionList;

    if (
      currentImageTaskActions?.length &&
      !currentImageTaskActions?.find((act) => act.id === selectedActionId)
    ) {
      updateSelectedImageActionId(currentImageTaskActions[0]?.id || 0);
    }
    return currentImageTaskActions;
  }, [selectedImageTaskId, imageTasks]);

  const selectedAction: ImageManagementActionDTO = useMemo(() => {
    if (selectedActionId) {
      const currentAction = actions?.find((actn) => actn.id === selectedActionId);
      return currentAction;
    }
  }, [selectedActionId, actions]);

  const storageList = useMemo(() => {
    const newStorageList = imageStoreList?.map((store) => {
      return {
        id: store.id,
        title: store.name + ' (' + store.availableSize + ' GB Free)',
        value: store.name,
      };
    });
    return newStorageList;
  }, [imageStoreList]);

  const onAddAction = (actionType: number) => {
    const newId = getNewId();
    const newAction: ImageManagementActionDTO = new ImageManagementActionDTO({
      id: newId,
      imageManagementId: selectedImageTaskId,
      rootSearchConstraintTreeNodeId: 3, // will change this after implementation of search paths
      actionType: actionType,
      sourceImageStoreId: storageList[0]?.id,
      targetImageStoreId: storageList[0]?.id,
      verify: false,
      excludeFirstInspection: false,
      excludeLastInspection: false,
      isExecuting: false,
      isDirty: false,
      isConstraintDirty: false,
      constraints: constraintsInitialState as SearchConstraintRootDTO,
    });
    onAddNewAction(newAction);
  };

  return (
    <div
      id="actions-wrapper"
      className="flex flex-column w-full h-[100%]"
      style={styles.mainContainerStyle}
    >
      <div
        id="actions-container"
        className="flex flex-column h-full"
        style={styles.subContianerStyle}
      >
        <div
          id="actions-topbar"
          className="flex flex-row justify-between items-center h-[10%]"
        >
          <div
            id="actions-title"
            className="text-[14px] text-[#000000] font-bold"
            style={{ fontFamily: 'Roboto' }}
          >
            Actions
          </div>
          <RMCustomButton
            title="ADD"
            color="#FFFFFF"
            titleClass="text-[14px]"
            iconProps={{ name: 'Add', fill: 'white', ...iconSize }}
            contentContainerStyle={{
              backgroundColor: actionButtonsEnable.add,
            }}
            titleTip="Add Action"
            isDisabled={selectedImageTaskId ? false : true}
            mainContainerClass="h-[68%]"
            showChevronIcon={true}
            showBorder={true}
            contentContainerClass="tree-control-bordered-action-button"
            dropDownList={[
              {
                label: 'Move',
                onClick: () => {
                  onAddAction(ActionOperationValue.Move);
                },
                isDisabled: false,
              },
              {
                label: 'Copy',
                onClick: () => {
                  onAddAction(ActionOperationValue.Copy);
                },
                isDisabled: false,
              },
              {
                label: 'Delete',
                onClick: () => {
                  onAddAction(ActionOperationValue.Delete);
                },
                isDisabled: false,
              },
            ]}
          />
        </div>
        <div
          id="actions-banners"
          className="flex flex-column h-[37%]"
          style={styles.taskBox}
        >
          <ActionsBanner
            actions={actions}
            selectedAction={selectedAction}
            storageList={storageList}
          />
        </div>
        <div
          id="actions-operation-title"
          className="flex flex-col justify-center h-[8%] text-[14px] font-bold"
          style={{ fontFamily: 'Roboto' }}
        >
          Operation
        </div>

        <div
          id="actions-operation-radioselector"
          className="flex flex-row justify-between items-center h-[6%]"
        >
          {renderRadioButtons(selectedAction, onChangeRadioActions)}
        </div>

        <div id="actions-operation-frominput-wrapper" className="flex flex-col h-[10%]">
          <div
            id="actions-operation-frominput-title"
            className="h-[50%]"
            style={{ fontFamily: 'Roboto' }}
          >
            From
          </div>
          <SelectInput
            options={storageList?.length ? storageList : [{ title: '', value: '' }]}
            style={styles.storageSelectBox}
            isDisabled={!selectedAction}
            value={
              storageList?.find((str) => str.id === selectedAction?.sourceImageStoreId)
                ?.value
            }
            onChange={(e) =>
              onChangeSourceImageStoreValue(
                storageList?.find((str) => str.value === e.target.value)?.id,
              )
            }
          />
        </div>

        <div id="actions-operation-toinput-wrapper" className="flex flex-col h-[10%]">
          <div
            id="actions-operation-toinput-title"
            className="h-[50%]"
            style={{ fontFamily: 'Roboto' }}
          >
            To
          </div>
          <SelectInput
            options={storageList?.length ? storageList : [{ title: '', value: '' }]}
            style={styles.storageSelectBox}
            value={
              storageList?.find((str) => str.id === selectedAction?.targetImageStoreId)
                ?.value
            }
            isDisabled={
              !selectedAction ||
              getCurrentOperationType(selectedAction) ===
                ActionOperationType[ActionOperationValue.Delete]
            }
            onChange={(e) =>
              onChangeTargetImageStoreValue(
                storageList?.find((str) => str.value === e.target.value)?.id,
              )
            }
          />
        </div>

        <CustomCheckBox
          onChange={onChangeVerifyValue}
          label={`Verify`}
          checked={selectedAction?.verify}
          labelStyle={{ fontFamily: 'Roboto', color: '#333333', marginLeft: '0.75rem' }}
          disabled={!selectedAction}
          mainContainerStyle={{ marginTop: '1%' }}
        />

        <div id="actions-exclude-wrapper" className="flex flex-col h-[10%] mt-[1%]">
          <div
            id="actions-exclude-title"
            className="text-[14px] h-[60%] font-bold"
            style={{ fontFamily: 'Roboto', fontWeight: '500' }}
          >
            Exclude
          </div>
          <div
            id="actions-exclude-checkboxes"
            className="flex flex-row justify-between items-center h-[40%]"
          >
            {renderInspectionCheckBoxes(selectedAction, onChangeInspectionCheckBoxValue)}
          </div>
        </div>
      </div>
    </div>
  );
};
const styles: IStyle = {
  mainContainerStyle: {
    height: '65vh',
  },
  subContianerStyle: {
    width: '98%',
  },
  taskBox: {
    border: '1px solid #DEE2E6',
    overflow: 'auto',
  },
  selectInput: {
    height: '2rem',
  },
  storageSelectBox: {
    height: '50%',
  },
  checkBox: {
    marginTop: '2%',
    marginBottom: '2%',
  },
  actionsBanner: {
    backgroundColor: '#DEE2E6',
    fontSize: 'smaller',
    borderRadius: '20px',
  },
};

export default Actions;
